import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft, Building2, Edit, Eye, Mail, Phone, Plus, RefreshCw, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { Layout } from "../components/Layout";
import { StatusBadge } from "../components/StatusBadge";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { usePopup } from "../components/AppPopups";
import { CommandBar, DataPanel, EmptyState, Surface, TableSurface } from "../components/CommandSystem";
import api, { formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { fmtDate, inr, PLAN_TYPES } from "../lib/format";

const TEXT_LIMIT = 30;
const ADDRESS_LIMIT = 50;

const EMPTY = {
  company_name: "",
  owner_name: "",
  mobile: "",
  email: "",
  password: "",
  address: "",
  gst_number: "",
  hoarding_limit: 50,
  plan_type: "Standard",
  subscription_amount: 9999,
  subscription_start: new Date().toISOString().slice(0, 10),
  subscription_expiry: new Date(Date.now() + 365 * 86400000).toISOString().slice(0, 10),
  status: "active",
};

const EMPTY_RENEWAL = {
  hoarding_limit: 500,
  plan_type: "Enterprise",
  subscription_amount: 0,
  subscription_start: new Date().toISOString().slice(0, 10),
  subscription_expiry: new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10),
  carry_forward_unused: true,
};

function fmtInt(value) {
  return Number(value || 0).toLocaleString("en-IN");
}

function addDaysInput(value, days) {
  const base = value ? new Date(value) : new Date();
  if (Number.isNaN(base.getTime())) return new Date().toISOString().slice(0, 10);
  base.setDate(base.getDate() + days);
  return base.toISOString().slice(0, 10);
}

function RequiredLabel({ children }) {
  return (
    <Label>
      {children} <span className="text-blue-500">*</span>
    </Label>
  );
}

function onlyLetters(value) {
  return value.replace(/[^A-Za-z\s]/g, "").replace(/\s{2,}/g, " ").slice(0, TEXT_LIMIT);
}

function onlyDigits(value, limit = TEXT_LIMIT) {
  return value.replace(/\D/g, "").slice(0, limit);
}

function normalizeForm(advertiser) {
  return {
    ...EMPTY,
    ...advertiser,
    password: "",
    subscription_start: (advertiser?.subscription_start || EMPTY.subscription_start).slice(0, 10),
    subscription_expiry: (advertiser?.subscription_expiry || EMPTY.subscription_expiry).slice(0, 10),
  };
}

function validateAdvertiser(form, editing) {
  const requiredFields = [
    ["Company Name", form.company_name],
    ["Owner Name", form.owner_name],
    ["Mobile", form.mobile],
    ["Email", form.email],
    ["Address", form.address],
    ["Plan Type", form.plan_type],
    ["Hoarding Limit", form.hoarding_limit],
    ["Subscription Amount", form.subscription_amount],
    ["Status", form.status],
    ["Subscription Start", form.subscription_start],
    ["Subscription Expiry", form.subscription_expiry],
  ];
  if (!editing) requiredFields.splice(4, 0, ["Password", form.password]);
  const missing = requiredFields.find(([, value]) => value == null || String(value).trim() === "");
  if (missing) return `${missing[0]} is required`;
  if (!/^[A-Za-z\s]+$/.test(form.owner_name.trim())) return "Owner name can contain only letters and spaces";
  if (!/^\d{10}$/.test(form.mobile)) return "Mobile number must be exactly 10 digits";
  if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email.trim())) return "Enter a valid email address";
  if (!editing && String(form.password).length < 6) return "Password must be at least 6 characters";
  return "";
}

export function Advertisers() {
  const nav = useNavigate();
  const { startGhostSession } = useAuth();
  const [items, setItems] = useState([]);
  const [search, setSearch] = useState("");

  async function load() {
    const { data } = await api.get("/admin/advertisers", { skipGlobalLoader: true });
    setItems(Array.isArray(data) ? data : []);
  }

  useEffect(() => { load(); }, []);

  const advertisers = Array.isArray(items) ? items : [];
  const filtered = advertisers.filter((i) => {
    if (!search) return true;
    const s = search.toLowerCase();
    return [i.company_name, i.owner_name, i.email, i.plan_type].some((v) => (v || "").toLowerCase().includes(s));
  });

  async function openGhostSession(event, advertiser) {
    event.stopPropagation();
    const result = await startGhostSession(advertiser.id);
    if (!result.ok) {
      toast.error(result.error || "Could not open ghost view");
      return;
    }
    toast.success(`Viewing ${advertiser.company_name} in read-only mode`);
    nav("/dashboard");
  }

  return (
    <Layout
      title="Advertiser Companies"
      subtitle={`${advertisers.length} account${advertisers.length !== 1 ? "s" : ""} on the platform`}
      actions={(
        <button data-testid="add-advertiser-btn" onClick={() => nav("/super-admin/advertisers/new")} className="hx-primary-action">
          <Plus size={16} /> New Advertiser
        </button>
      )}
    >
      <div className="space-y-5">
        <CommandBar title="Search">
          <div className="flex min-w-[220px] max-w-xs flex-1 items-center gap-2 rounded-lg border bg-white px-3 py-2 shadow-sm" style={{ borderColor: "var(--hx-border)" }}>
            <Search size={14} className="flex-shrink-0 text-slate-400" />
            <input
              data-testid="advertisers-search"
              placeholder="Search by company, email, plan..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="w-full bg-transparent text-sm outline-none placeholder:text-slate-400"
            />
          </div>
        </CommandBar>

        <DataPanel title="Advertiser accounts" subtitle="Plan, inventory usage, billing, and status for every advertiser company.">
          {filtered.length === 0 ? (
            <EmptyState title="No advertisers yet" description="Create the first advertiser company to get started." />
          ) : (
            <TableSurface>
              <table className="w-full table-fixed text-sm">
                <colgroup>
                  <col className="w-[12%]" />
                  <col className="w-[10%]" />
                  <col className="w-[19%]" />
                  <col className="w-[10%]" />
                  <col className="w-[13%]" />
                  <col className="w-[11%]" />
                  <col className="w-[9%]" />
                  <col className="w-[8%]" />
                  <col className="w-[6%]" />
                  <col className="w-[6%]" />
                </colgroup>
                <thead>
                  <tr>
                    <th>Company</th>
                    <th>Owner</th>
                    <th>Email</th>
                    <th>Plan</th>
                    <th>Hoardings</th>
                    <th>Days left</th>
                    <th>MRR</th>
                    <th>Expiry</th>
                    <th>Status</th>
                    <th>View</th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((a) => {
                    const pct = a.hoarding_limit ? Math.min(100, Math.round((a.hoardings_used / a.hoarding_limit) * 100)) : 0;
                    return (
                      <tr
                        key={a.id}
                        data-testid={`advertiser-row-${a.id}`}
                        onClick={() => nav(`/super-admin/advertisers/${a.id}`)}
                        className="cursor-pointer"
                      >
                        <td className="font-medium text-slate-800">
                          <div className="truncate" title={a.company_name}>{a.company_name}</div>
                        </td>
                        <td className="text-slate-600">
                          <div className="truncate" title={a.owner_name}>{a.owner_name}</div>
                        </td>
                        <td className="text-slate-600">
                          <div className="truncate" title={a.email}>{a.email}</div>
                        </td>
                        <td>
                          <span className="inline-flex max-w-full rounded-full border border-blue-100 bg-blue-50 px-2 py-0.5 text-xs font-medium text-blue-700">
                            <span className="truncate">{a.plan_type}</span>
                          </span>
                        </td>
                        <td>
                          <div className="flex min-w-0 items-center gap-2">
                            <div className="shrink-0 font-mono text-xs text-slate-700">{a.hoardings_used}/{a.hoarding_limit}</div>
                            <div className="h-1.5 min-w-0 flex-1 overflow-hidden rounded-full bg-slate-100">
                              <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
                            </div>
                          </div>
                        </td>
                        <td className="font-mono text-xs font-semibold text-slate-700">{fmtInt(a.hoarding_days?.remaining_days)}</td>
                        <td className="hx-money-cell">{inr(a.subscription_amount)}</td>
                        <td className="hx-date-cell">{fmtDate(a.subscription_expiry)}</td>
                        <td>
                          <StatusBadge status={a.status} />
                        </td>
                        <td>
                          <button
                            type="button"
                            onClick={(event) => openGhostSession(event, a)}
                            className="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-blue-100 bg-blue-50 text-blue-700 transition hover:bg-blue-100"
                            title="View account as read-only ghost session"
                          >
                            <Eye size={15} />
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </TableSurface>
          )}
        </DataPanel>
      </div>
    </Layout>
  );
}

export function AdvertiserFormPage({ mode = "create" }) {
  const nav = useNavigate();
  const { id } = useParams();
  const editing = mode === "edit";
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!editing || !id) return;
    api.get(`/admin/advertisers/${id}`, { skipGlobalLoader: true })
      .then(({ data }) => setForm(normalizeForm(data)))
      .catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not load advertiser"));
  }, [editing, id]);

  async function save() {
    const error = validateAdvertiser(form, editing);
    if (error) {
      toast.error(error);
      return;
    }

    setLoading(true);
    try {
      if (editing) {
        const upd = { ...form };
        delete upd.password;
        delete upd.email;
        await api.put(`/admin/advertisers/${id}`, upd);
        toast.success("Advertiser updated");
        nav(`/super-admin/advertisers/${id}`);
      } else {
        const { data } = await api.post("/admin/advertisers", {
          ...form,
          email: form.email.trim().toLowerCase(),
          owner_name: form.owner_name.trim(),
          company_name: form.company_name.trim(),
          address: form.address.trim(),
        });
        toast.success("Advertiser created");
        nav(`/super-admin/advertisers/${data.id}`);
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  }

  return (
    <Layout
      title={editing ? "Edit Advertiser" : "New Advertiser"}
      subtitle={editing ? "Update plan, status, and account limits." : "Onboard a new advertiser company."}
      actions={(
        <button type="button" onClick={() => nav(editing ? `/super-admin/advertisers/${id}` : "/super-admin/advertisers")} className="hx-secondary-action">
          <ArrowLeft size={16} /> Back
        </button>
      )}
    >
      <DataPanel title="Advertiser details" subtitle="Required fields are marked before saving the account.">
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
          <Field label="Company Name" required>
            <Input data-testid="adv-company" required maxLength={TEXT_LIMIT} value={form.company_name} onChange={(e) => setForm({ ...form, company_name: e.target.value.slice(0, TEXT_LIMIT) })} />
          </Field>
          <Field label="Owner Name" required>
            <Input data-testid="adv-owner" required maxLength={TEXT_LIMIT} value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: onlyLetters(e.target.value) })} />
          </Field>
          <Field label="Mobile" required>
            <Input data-testid="adv-mobile" required inputMode="numeric" value={form.mobile} onChange={(e) => setForm({ ...form, mobile: onlyDigits(e.target.value, 10) })} />
          </Field>
          <Field label="Email" required>
            <Input data-testid="adv-email" required type="email" maxLength={TEXT_LIMIT} disabled={editing} value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value.slice(0, TEXT_LIMIT) })} />
          </Field>
          {!editing && (
            <Field label="Password" required>
              <Input data-testid="adv-password" required type="text" maxLength={TEXT_LIMIT} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value.slice(0, TEXT_LIMIT) })} />
            </Field>
          )}
          <Field label="GST Number">
            <Input maxLength={TEXT_LIMIT} value={form.gst_number} onChange={(e) => setForm({ ...form, gst_number: e.target.value.slice(0, TEXT_LIMIT) })} />
          </Field>
          <Field label={`Address (${Math.max(0, ADDRESS_LIMIT - (form.address || "").length)} left)`} required className="sm:col-span-2">
            <Input required maxLength={ADDRESS_LIMIT} value={form.address} onChange={(e) => setForm({ ...form, address: e.target.value.slice(0, ADDRESS_LIMIT) })} />
          </Field>
          <Field label="Plan Type" required>
            <Select value={form.plan_type} onValueChange={(v) => setForm({ ...form, plan_type: v })}>
              <SelectTrigger data-testid="adv-plan"><SelectValue /></SelectTrigger>
              <SelectContent>{PLAN_TYPES.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
            </Select>
          </Field>
          <Field label="Hoarding Limit" required>
            <Input data-testid="adv-limit" required inputMode="numeric" value={form.hoarding_limit} onChange={(e) => setForm({ ...form, hoarding_limit: parseInt(onlyDigits(e.target.value, 5) || 0, 10) })} />
          </Field>
          <Field label="Subscription Amount" required>
            <Input required inputMode="numeric" value={form.subscription_amount} onChange={(e) => setForm({ ...form, subscription_amount: parseFloat(onlyDigits(e.target.value, 7) || 0) })} />
          </Field>
          <Field label="Status" required>
            <div className="flex h-10 items-center gap-3">
              <Switch checked={form.status === "active"} onCheckedChange={(checked) => setForm({ ...form, status: checked ? "active" : "inactive" })} />
              <span className="text-sm capitalize">{form.status}</span>
            </div>
          </Field>
          <Field label="Subscription Start" required>
            <Input required type="date" value={form.subscription_start} onChange={(e) => setForm({ ...form, subscription_start: e.target.value })} />
          </Field>
          <Field label="Subscription Expiry" required>
            <Input required type="date" value={form.subscription_expiry} onChange={(e) => setForm({ ...form, subscription_expiry: e.target.value })} />
          </Field>
        </div>
        <div className="mt-5 flex justify-end gap-2 border-t pt-4">
          <Button variant="outline" onClick={() => nav(editing ? `/super-admin/advertisers/${id}` : "/super-admin/advertisers")}>Cancel</Button>
          <Button data-testid="adv-save" onClick={save} disabled={loading}>
            {loading ? "Saving..." : editing ? "Update Advertiser" : "Create Advertiser"}
          </Button>
        </div>
      </DataPanel>
    </Layout>
  );
}

export function AdvertiserDetail() {
  const nav = useNavigate();
  const popup = usePopup();
  const { startGhostSession } = useAuth();
  const { id } = useParams();
  const [advertiser, setAdvertiser] = useState(null);
  const [renewal, setRenewal] = useState(EMPTY_RENEWAL);
  const [renewing, setRenewing] = useState(false);
  const [lapsing, setLapsing] = useState(false);

  const load = useCallback(async () => {
    const { data } = await api.get(`/admin/advertisers/${id}`, { skipGlobalLoader: true });
    setAdvertiser(data);
  }, [id]);

  useEffect(() => {
    load().catch((err) => toast.error(formatApiError(err.response?.data?.detail) || "Could not load advertiser"));
  }, [load]);

  useEffect(() => {
    if (!advertiser) return;
    const minStart = addDaysInput(advertiser.subscription_expiry, 1);
    setRenewal((current) => ({
      ...current,
      hoarding_limit: advertiser.hoarding_limit || current.hoarding_limit,
      plan_type: advertiser.plan_type || current.plan_type,
      subscription_amount: advertiser.subscription_amount || current.subscription_amount,
      subscription_start: minStart,
      subscription_expiry: addDaysInput(minStart, 30),
    }));
  }, [advertiser]);

  async function toggleStatus() {
    if (!advertiser) return;
    const next = advertiser.status === "active" ? "inactive" : "active";
    await api.put(`/admin/advertisers/${advertiser.id}`, { status: next });
    toast.success(`Advertiser ${next}`);
    load();
  }

  async function remove() {
    if (!advertiser) return;
    const ok = await popup.confirm({
      title: `Delete ${advertiser.company_name}?`,
      description: "This removes the advertiser account, users, and hoardings from active records.",
      confirmText: "Delete Advertiser",
      tone: "danger",
    });
    if (!ok) return;
    await api.delete(`/admin/advertisers/${advertiser.id}`);
    toast.success("Advertiser deleted");
    nav("/super-admin/advertisers");
  }

  async function renewSubscription() {
    if (!advertiser) return;
    if (!renewal.hoarding_limit || Number(renewal.hoarding_limit) <= 0) {
      toast.error("Hoarding limit is required");
      return;
    }
    if (!renewal.subscription_start || !renewal.subscription_expiry) {
      toast.error("Subscription start and expiry are required");
      return;
    }
    const minStart = addDaysInput(advertiser.subscription_expiry, 1);
    if (renewal.subscription_start < minStart) {
      toast.error(`Renewal must start on or after ${fmtDate(minStart)}`);
      return;
    }
    if (renewal.subscription_expiry <= renewal.subscription_start) {
      toast.error("Expiry must be after renewal start");
      return;
    }
    const ok = await popup.confirm({
      title: "Renew subscription?",
      description: `This will start a new subscription from ${fmtDate(renewal.subscription_start)} to ${fmtDate(renewal.subscription_expiry)} with ${fmtInt(renewal.hoarding_limit)} hoardings.`,
      confirmText: "Renew Subscription",
    });
    if (!ok) return;
    setRenewing(true);
    try {
      const { data } = await api.post(`/admin/advertisers/${advertiser.id}/renew`, {
        ...renewal,
        hoarding_limit: Number(renewal.hoarding_limit),
        subscription_amount: Number(renewal.subscription_amount || 0),
      });
      setAdvertiser(data);
      toast.success("Subscription renewed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Renewal failed");
    } finally {
      setRenewing(false);
    }
  }

  async function lapseHoardingDays() {
    if (!advertiser) return;
    const remaining = Number(advertiser.hoarding_days?.remaining_days || 0);
    if (remaining <= 0) {
      toast.info("No available hoarding days to lapse");
      return;
    }
    const ok = await popup.confirm({
      title: "Lapse available hoarding days?",
      description: `This will remove ${fmtInt(remaining)} available hoarding day${remaining === 1 ? "" : "s"} from this advertiser's active subscription. This cannot be used later as carry forward.`,
      confirmText: "Lapse Days",
      tone: "danger",
    });
    if (!ok) return;
    setLapsing(true);
    try {
      const { data } = await api.post(`/admin/advertisers/${advertiser.id}/lapse-hoarding-days`, {
        reason: "Lapsed by superadmin",
      });
      setAdvertiser(data);
      toast.success("Available hoarding days lapsed");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not lapse hoarding days");
    } finally {
      setLapsing(false);
    }
  }

  async function openGhostSession() {
    if (!advertiser) return;
    const result = await startGhostSession(advertiser.id);
    if (!result.ok) {
      toast.error(result.error || "Could not open ghost view");
      return;
    }
    toast.success(`Viewing ${advertiser.company_name} in read-only mode`);
    nav("/dashboard");
  }

  if (!advertiser) {
    return <Layout title="Advertiser" subtitle="Loading account details..." />;
  }

  const pct = advertiser.hoarding_limit ? Math.min(100, Math.round((advertiser.hoardings_used / advertiser.hoarding_limit) * 100)) : 0;
  const hoardingDays = advertiser.hoarding_days || {};
  const dayPct = hoardingDays.total_days ? Math.min(100, Math.round((Number(hoardingDays.used_days || 0) / Number(hoardingDays.total_days || 1)) * 100)) : 0;
  const minRenewalStart = addDaysInput(advertiser.subscription_expiry, 1);

  return (
    <Layout
      title={advertiser.company_name}
      subtitle="Advertiser account details"
      actions={(
        <>
          <button type="button" onClick={() => nav("/super-admin/advertisers")} className="hx-secondary-action">
            <ArrowLeft size={16} /> Back
          </button>
          <button type="button" onClick={openGhostSession} className="hx-secondary-action">
            <Eye size={16} /> Ghost View
          </button>
          <button type="button" onClick={() => nav(`/super-admin/advertisers/${advertiser.id}/edit`)} className="hx-primary-action">
            <Edit size={16} /> Edit
          </button>
        </>
      )}
    >
      <div className="space-y-5">
      <div className="grid grid-cols-1 items-stretch gap-5 lg:grid-cols-[minmax(0,1fr)_22rem]">
        <DataPanel title="Company profile" subtitle="Owner, contact, and subscription information.">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <DetailItem label="Company" value={advertiser.company_name} icon={Building2} />
            <DetailItem label="Owner" value={advertiser.owner_name} />
            <DetailItem label="Email" value={advertiser.email} icon={Mail} />
            <DetailItem label="Mobile" value={advertiser.mobile} icon={Phone} />
            <DetailItem label="GST Number" value={advertiser.gst_number || "-"} />
            <DetailItem label="Address" value={advertiser.address || "-"} className="sm:col-span-2" />
          </div>
        </DataPanel>

        <div className="space-y-5">
          <Surface className="p-5">
            <div className="flex items-start justify-between gap-3">
              <div>
                <p className="text-xs font-semibold uppercase text-slate-500">Status</p>
                <div className="mt-2"><StatusBadge status={advertiser.status} /></div>
              </div>
              <Button variant="outline" size="sm" onClick={toggleStatus}>
                Mark {advertiser.status === "active" ? "inactive" : "active"}
              </Button>
            </div>
          </Surface>
          <Surface className="space-y-4 p-5">
            <DetailLine label="Plan" value={advertiser.plan_type} />
            <DetailLine label="MRR" value={inr(advertiser.subscription_amount)} />
            <DetailLine label="Start" value={fmtDate(advertiser.subscription_start)} />
            <DetailLine label="Expiry" value={fmtDate(advertiser.subscription_expiry)} />
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">Hoardings</span>
                <span className="font-mono font-semibold text-slate-800">{advertiser.hoardings_used}/{advertiser.hoarding_limit}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-blue-500" style={{ width: `${pct}%` }} />
              </div>
            </div>
            <div>
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-medium text-slate-500">Available hoarding days</span>
                <span className="font-mono font-semibold text-slate-800">{fmtInt(hoardingDays.remaining_days)}</span>
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-slate-100">
                <div className="h-full bg-emerald-500" style={{ width: `${dayPct}%` }} />
              </div>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs text-slate-500">
                <span>Used: <b className="text-slate-800">{fmtInt(hoardingDays.used_days)}</b></span>
                <span>Total: <b className="text-slate-800">{fmtInt(hoardingDays.total_days)}</b></span>
                <span>Today burn: <b className="text-slate-800">{fmtInt(hoardingDays.today_used_days)}</b></span>
                <span>Carry: <b className="text-slate-800">{fmtInt(hoardingDays.carry_forward_days)}</b></span>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="mt-3 w-full border-rose-200 text-rose-700 hover:bg-rose-50"
                onClick={lapseHoardingDays}
                disabled={lapsing || Number(hoardingDays.remaining_days || 0) <= 0}
              >
                {lapsing ? "Lapsing..." : "Lapse available days"}
              </Button>
            </div>
          </Surface>
          <Surface className="p-5">
            <Button variant="destructive" className="w-full" onClick={remove}>
              <Trash2 size={16} /> Delete Advertiser
            </Button>
          </Surface>
        </div>
      </div>
        <Surface className="space-y-4 p-5">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-2 text-sm font-bold text-slate-950">
                <RefreshCw size={15} /> Renew hoarding days
              </div>
              <p className="mt-1 text-xs text-slate-500">Create a fresh subscription period and choose what happens to unused days.</p>
            </div>
            <Button className="w-full lg:w-auto" onClick={renewSubscription} disabled={renewing}>
              {renewing ? "Renewing..." : "Renew Subscription"}
            </Button>
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2 xl:grid-cols-6">
            <Field label="Limit" required>
              <Input inputMode="numeric" value={renewal.hoarding_limit} onChange={(e) => setRenewal({ ...renewal, hoarding_limit: parseInt(onlyDigits(e.target.value, 6) || 0, 10) })} />
            </Field>
            <Field label="Amount">
              <Input inputMode="numeric" value={renewal.subscription_amount} onChange={(e) => setRenewal({ ...renewal, subscription_amount: parseFloat(onlyDigits(e.target.value, 9) || 0) })} />
            </Field>
            <Field label="Start" required>
              <Input type="date" min={minRenewalStart} value={renewal.subscription_start} onChange={(e) => setRenewal({ ...renewal, subscription_start: e.target.value, subscription_expiry: renewal.subscription_expiry <= e.target.value ? addDaysInput(e.target.value, 30) : renewal.subscription_expiry })} />
            </Field>
            <Field label="Expiry" required>
              <Input type="date" min={addDaysInput(renewal.subscription_start, 1)} value={renewal.subscription_expiry} onChange={(e) => setRenewal({ ...renewal, subscription_expiry: e.target.value })} />
            </Field>
            <Field label="Plan">
              <Select value={renewal.plan_type} onValueChange={(value) => setRenewal({ ...renewal, plan_type: value })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{PLAN_TYPES.map((plan) => <SelectItem key={plan} value={plan}>{plan}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <label className="flex min-h-10 items-center justify-between gap-3 rounded-lg border border-slate-200 px-3 py-2 text-sm">
              <span>
                <b className="block text-xs text-slate-900">Carry unused</b>
                <span className="text-[11px] text-slate-500">Off lapses previous balance.</span>
              </span>
              <Switch checked={renewal.carry_forward_unused} onCheckedChange={(checked) => setRenewal({ ...renewal, carry_forward_unused: checked })} />
            </label>
          </div>
        </Surface>
      </div>
    </Layout>
  );
}

function Field({ label, required = false, children, className = "" }) {
  return (
    <div className={`space-y-1 ${className}`}>
      {required ? <RequiredLabel>{label}</RequiredLabel> : <Label>{label}</Label>}
      {children}
    </div>
  );
}

function DetailItem({ label, value, icon: Icon, className = "" }) {
  return (
    <div className={`rounded-lg border border-slate-200 bg-white p-4 ${className}`}>
      <div className="flex items-center gap-2 text-xs font-semibold uppercase text-slate-500">
        {Icon && <Icon size={14} />}
        {label}
      </div>
      <p className="mt-2 break-words text-sm font-semibold text-slate-900">{value}</p>
    </div>
  );
}

function DetailLine({ label, value }) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <span className="text-slate-500">{label}</span>
      <span className="font-semibold text-slate-900">{value}</span>
    </div>
  );
}
